// Cal AI as a first-class tab (owner, 2026-08-09) — the assistant moved out
// of the Today sheet into the bottom navigation.
//
// UX decisions carried over from the sheet it replaces:
// • Cal IS the interface: greeting when idle, curious while you type,
//   thinking while the server works — no spinners.
// • Suggestion chips come from the user's ACTUAL day (fetched here, since a
//   tab has no Today page to hand it a snapshot).
// • Safety answers arrive as a KEY and render from the locale files.
// • The medical disclaimer stays visible under the input.
// The conversation survives tab switches for the session (module store) but
// is deliberately not persisted — a fresh launch is a fresh conversation.
import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../lib/api";
import { useT, useLocale, apiLang } from "../lib/i18n";
import { todayLocal } from "../lib/date";
import { Mascot } from "../components/Mascot";
import { Paywall } from "../components/Paywall";
import type { MascotPose } from "../lib/mascot";
import type { TodayResponse } from "@shared/types";

interface Turn {
  q: string;
  a: string | null;
  safetyKey?: string;
  failed?: boolean;
}

// Session memory: switching to Progress and back must not eat the chat.
let SESSION_TURNS: Turn[] = [];

export function CalAI() {
  const t = useT();
  const { locale } = useLocale();
  const [turns, setTurnsState] = useState<Turn[]>(SESSION_TURNS);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [paywall, setPaywall] = useState(false);
  const [today, setToday] = useState<TodayResponse | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const setTurns = (fn: (prev: Turn[]) => Turn[]) => {
    setTurnsState((prev) => {
      SESSION_TURNS = fn(prev);
      return SESSION_TURNS;
    });
  };

  // The day snapshot that powers the chips and grounds the assistant.
  useEffect(() => {
    let cancelled = false;
    api.today(todayLocal(), apiLang(locale))
      .then((d) => { if (!cancelled) setToday(d); })
      .catch(() => { /* chips degrade; asking still works */ });
    return () => { cancelled = true; };
  }, [locale]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [turns, busy]);

  const pose: MascotPose = busy ? "thinking"
    : turns.length === 0 ? "greeting"
    : turns[turns.length - 1]?.safetyKey ? "sheepish"
    : turns[turns.length - 1]?.failed ? "sheepish"
    : draft.trim() ? "curious"
    : "happy";

  /** Chips from the user's ACTUAL day, most specific first — three at most. */
  const chips: string[] = [];
  if (today) {
    const remaining = Math.max(0, today.budget_kcal - today.consumed.calories);
    const proteinLeft = Math.max(0, today.goals.protein_g - (today.consumed.protein_g ?? 0));
    if (today.last_adjustment) chips.push(t("assistant.chip_target"));
    if (today.consumed.calories === 0) chips.push(t("assistant.chip_start"));
    else if (remaining > 0) chips.push(t("assistant.chip_left_n", { kcal: String(remaining) }));
    if (proteinLeft > 30 && today.consumed.calories > 0) {
      chips.push(t("assistant.chip_protein_n", { g: String(proteinLeft) }));
    }
    if (!today.weighed_today && chips.length < 3) chips.push(t("assistant.chip_weight"));
  }
  chips.push(t("assistant.chip_progress"));
  chips.splice(3);

  async function ask(question: string) {
    const q = question.trim();
    if (!q || busy) return;
    setDraft("");
    setTurns((prev) => [...prev, { q, a: null }]);
    setBusy(true);
    try {
      const r = await api.askAssistant(q, today?.date);
      setTurns((prev) => prev.map((turn, i) =>
        i === prev.length - 1 ? { ...turn, a: r.answer ?? null, safetyKey: r.safety_key } : turn));
    } catch (e) {
      if (e instanceof ApiError && e.message === "premium_required") {
        setTurns((prev) => prev.slice(0, -1));
        setPaywall(true);
      } else {
        setTurns((prev) => prev.map((turn, i) =>
          i === prev.length - 1 ? { ...turn, failed: true } : turn));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    // The composer is FIXED to the bottom of the screen (classic chat, owner)
    // — a percentage-height page can never reach past the shell's tab-bar
    // padding, so pinning is the only honest way down there. The message list
    // stays in normal flow and pads its bottom to scroll clear of the bar.
    <div className="px-0">
      <header className="flex items-center gap-3 pt-4 pb-3">
        {(turns.length > 0 || busy) && <Mascot pose={pose} size={44} />}
        <div>
          <h1 className="text-lg font-semibold">{t("assistant.title")}</h1>
          <p className="text-sm text-fg-muted">{t("assistant.subtitle")}</p>
        </div>
      </header>

      <div className="space-y-3 py-1 pb-24">
        {turns.length === 0 && (
          // Empty state: Cal front and centre, one line of what this is, then
          // the day-grounded suggestion chips.
          <div className="flex flex-col items-center text-center gap-3 pt-6 pb-2">
            <Mascot pose={pose} size={96} />
            <p className="text-sm text-fg-secondary leading-relaxed max-w-xs">{t("assistant.intro")}</p>
            <div className="flex flex-wrap justify-center gap-2 pt-3">
              {chips.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => ask(c)}
                  className="rounded-full bg-surface px-3 py-2 text-sm text-fg-secondary press"
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}
        {turns.map((turn, i) => (
          <div key={i} className="space-y-2">
            <div className="ml-auto max-w-[85%] w-fit bg-brand/15 text-fg rounded-2xl rounded-br-md px-3 py-2 text-sm">
              {turn.q}
            </div>
            {turn.a || turn.safetyKey || turn.failed ? (
              <div className="max-w-[92%] bg-surface rounded-2xl rounded-bl-md px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap">
                {turn.failed ? t("assistant.failed") : turn.safetyKey ? t(turn.safetyKey) : turn.a}
              </div>
            ) : (
              <div className="flex gap-1 px-3 py-3" aria-label={t("assistant.thinking")}>
                <span className="w-2 h-2 rounded-full bg-fg-faint animate-pulse" />
                <span className="w-2 h-2 rounded-full bg-fg-faint animate-pulse [animation-delay:150ms]" />
                <span className="w-2 h-2 rounded-full bg-fg-faint animate-pulse [animation-delay:300ms]" />
              </div>
            )}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {/* Pinned composer — sits directly on top of the tab bar, full width,
          frosted so messages scrolling beneath stay readable. The disclaimer
          lives inside the bar, small, under the input. */}
      <div className="fixed inset-x-0 z-30 px-5 pt-2 pb-2 bg-canvas/95 [backdrop-filter:blur(12px)] [-webkit-backdrop-filter:blur(12px)] border-t border-line [bottom:calc(4.4rem+env(safe-area-inset-bottom))]">
        <form
          onSubmit={(e) => { e.preventDefault(); void ask(draft); }}
          className="flex items-end gap-2"
        >
          <input
            className="input flex-1"
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, 500))}
            placeholder={t("assistant.placeholder")}
            aria-label={t("assistant.placeholder")}
          />
          <button
            type="submit"
            disabled={busy || !draft.trim()}
            className="h-11 px-4 rounded-full bg-brand text-on-brand font-medium text-sm disabled:opacity-50 press"
          >
            {t("assistant.send")}
          </button>
        </form>
        <p className="pt-1.5 text-[0.8rem] text-fg-faint leading-snug">{t("assistant.disclaimer")}</p>
      </div>

      {paywall && <Paywall trigger="feature" onClose={() => setPaywall(false)} />}
    </div>
  );
}
